// @ts-nocheck
/**
 * WebViewNavBar — responsive action bar for /web/ and preview pages.
 *
 * Breakpoints:
 *   ≥ 640 px  — horizontal row, every button shows icon + label
 *   < 640 px  — hamburger (☰) on left, remaining buttons icon-only;
 *               hamburger opens a flyout showing all buttons with labels
 *               (DigiGuide launch + TOC + all nav buttons)
 *   < 400 px  — only hamburger + DCI-logo link + PDF icon + manual logo/title
 *               visible; everything else lives in the hamburger flyout
 *
 * Only one flyout open at a time: 'menu' (nav list), 'toc', or 'lang'.
 *
 * Props — all optional with sensible fallbacks:
 *   manual          — resolved manual object
 *   linkCard        — parsed link card (appUrl, pdfUrl, linkUrl, webUrl, chapterId…)
 *   isPreview       — boolean
 *   isProductionView — boolean
 *   isLinkMode      — boolean (?link=digiguide in URL)
 *   chapterGroups   — Array<{heading,topics}> built from heading scan in ManualWebPage
 *   t               — translation lookup fn(key, fallback)
 *   breadcrumbContent — React node — breadcrumb bar to render below icons or inside hamburger
 *
 *   Button-specific:
 *   pdfUrl, webUrl, linkUrl, appUrl  — passed through from linkCard
 *   onProdToggle    — fn()
 *   onParkPreview   — fn()
 *   buildProdToggleUrl — fn(bool) → string
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import NavButton from "./NavButton";
import TocPanel from "./TocPanel";
import LangPanel from "./LangPanel";
import ExpandPanel from "./ExpandPanel";
import dciLogo from "../images/DCI.png";

const font = "'Open Sans', system-ui, sans-serif";

// ── Icons ──────────────────────────────────────────────────────────────────

const HamburgerIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: "block", flexShrink: 0 }}>
    <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: "block", flexShrink: 0 }}>
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const OpenBookIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: "block", flexShrink: 0 }}>
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);

const ClosedBookIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: "block", flexShrink: 0 }}>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: "block", flexShrink: 0 }}>
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const PdfIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: "block", flexShrink: 0 }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="13" y2="17" />
  </svg>
);

const LinkIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: "block", flexShrink: 0 }}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: "block", flexShrink: 0 }}>
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);

const DciBox = ({ size = 15 }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: size, height: size, background: "#0B83FF", borderRadius: "3px", flexShrink: 0,
  }}>
    <img src={dciLogo} alt="DCI" style={{ width: size * 0.7, height: size * 0.7, objectFit: "contain", display: "block" }} />
  </span>
);

const EyeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: "block", flexShrink: 0 }}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const ProdIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: "block", flexShrink: 0 }}>
    <rect x="4" y="3" width="16" height="12" rx="1" />
    <line x1="4" y1="8" x2="20" y2="8" />
    <line x1="12" y1="3" x2="12" y2="8" />
    <line x1="1" y1="18" x2="23" y2="18" />
    <circle cx="5" cy="21" r="2" /><circle cx="12" cy="21" r="2" /><circle cx="19" cy="21" r="2" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ display: "block", flexShrink: 0 }}>
    <polyline points="1 4 1 10 7 10" />
    <polyline points="23 20 23 14 17 14" />
    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
  </svg>
);

const ImportExportIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: "block", flexShrink: 0 }}>
    <polyline points="8 17 12 21 16 17" /><line x1="12" y1="12" x2="12" y2="21" />
    <polyline points="8 7 12 3 16 7" /><line x1="12" y1="3" x2="12" y2="12" />
  </svg>
);

const ExternalArrowIcon = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    style={{ display: "block", flexShrink: 0 }}>
    <line x1="7" y1="17" x2="17" y2="7" />
    <polyline points="7 7 17 7 17 17" />
  </svg>
);

const GoogleTranslateMenuIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="#4285F4" style={{ display: "block", flexShrink: 0 }}>
    <path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0 0 14.07 6H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" />
  </svg>
);

// ── WebViewNavBar ──────────────────────────────────────────────────────────

export default function WebViewNavBar({
  manual,
  linkCard,
  isPreview = false,
  isProductionView = false,
  isParked = false,
  isLinkMode = false,
  chapterGroups = [],
  t = (k, fb) => fb,
  siteId,
  allSitesForSelector = [],
  onLangChange,
  buildLangUrl,
  buildProdToggleUrl,
  onToggleLivePreview,
  breadcrumbContent,
}) {
  const [openPanel, setOpenPanel] = useState(null); // null | 'menu' | 'toc' | 'lang'
  const [tocSubOpen, setTocSubOpen] = useState(true);
  const [langSubOpen, setLangSubOpen] = useState(false);
  const navRef = useRef(null);
  const tocButtonRef = useRef(null);
  const langButtonRef = useRef(null);

  const toggle = useCallback((panel) => {
    setOpenPanel(prev => prev === panel ? null : panel);
  }, []);

  const close = useCallback(() => { setOpenPanel(null); }, []);

  // ── Responsive width detection ───────────────────────────────────────────
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isCompact = width < 640;  // icon-only buttons visible, hamburger shown
  const isMicro   = width < 400;  // only hamburger + DCI + PDF + logo/title visible

  // Close hamburger on breakpoint change to non-compact
  useEffect(() => {
    if (!isCompact) setOpenPanel(prev => prev === "menu" ? null : prev);
  }, [isCompact]);

  // Reset tocSubOpen when hamburger opens
  useEffect(() => {
    if (openPanel === "menu") {
      setTocSubOpen(true);
      setLangSubOpen(false);
    }
  }, [openPanel]);

  if (!linkCard) return null;

  const { appUrl, pdfUrl, linkUrl, webUrl, previewUrl, chapterId, topicId, chapterTitle, topicTitle } = linkCard;
  const manualId = manual?.id;
  const manualTitle = manual?.userTitle || manual?.title || "";
  const logoUrl = manual?.logo?.url;

  const isProdMode = isPreview
    ? (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("production") === "1")
    : false;
  const isLivePreviewActive = isPreview && !isParked;

  // ── Labels from translations ─────────────────────────────────────────────
  const contentsLabel  = t("web_contents", "Contents");
  const webLabel       = t("web_web", "");
  const universalLabel = t("web_universal", "");
  const manualsLabel   = t("search_all_manuals", "Manuals List");
  const langLabel      = t("screen_language", "Language");
  const launchLabel    = t("link_click_launch", "Launch");
  const manualLabel    = t("screen_manual", "Manual");

  // showLabel: true at ≥640, false below
  const showLabel = !isCompact;

  // ── Button definitions ───────────────────────────────────────────────────
  const buttons = [
    {
      id: "home",
      icon: <ClosedBookIcon />,
      label: manualsLabel,
      href: "/",
      title: manualsLabel,
      hideAtMicro: true,
    },
    ...(pdfUrl ? [{
      id: "pdf",
      icon: <PdfIcon />,
      label: "PDF ↗",
      href: pdfUrl,
      external: true,
      title: t("web_pdf_download", "Download PDF"),
      hideAtMicro: false,
    }] : []),
    {
      id: "web",
      icon: <GlobeIcon />,
      label: webLabel ? `${webLabel} ↗` : (isPreview ? "Web ↗" : "↗"),
      href: webUrl,
      onClick: !isPreview ? (e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); } : undefined,
      title: t("web_web", "Web"),
      hideAtMicro: true,
    },
    ...(isPreview && previewUrl ? [{
      id: "preview",
      icon: <EyeIcon />,
      label: "Live Preview →",
      href: previewUrl,
      title: "Open in web preview mode",
      style: { color: "#6600aa", background: "#ede5f7" },
      hideAtMicro: true,
    }] : []),
    ...(linkUrl ? [{
      id: "universal",
      icon: <LinkIcon />,
      label: universalLabel ? `${universalLabel} ↗` : "↗",
      href: linkUrl,
      title: universalLabel || "Universal link",
      hideAtMicro: true,
    }] : []),
    ...(isPreview ? [
      {
        id: "prod",
        icon: <ProdIcon />,
        label: "Prod",
        href: buildProdToggleUrl ? buildProdToggleUrl(!isProdMode) : "#",
        title: isProdMode ? "Switch to live preview" : "Switch to production view",
        active: isProdMode,
        style: isProdMode
          ? { color: "#166534", background: "#dcfce7", border: "1px solid #86efac" }
          : isLivePreviewActive ? { color: "#bbb", background: "#f0f0f0" } : { background: "#dce5f7" },
        hideAtMicro: true,
      },
      {
        id: "importexport",
        icon: <ImportExportIcon />,
        label: "I/E",
        href: "/importexport",
        title: "Import / Export",
        hideAtMicro: true,
      },
      ...(isLivePreviewActive && linkCard?.pdfUrl ? [{
        id: "livepdf",
        icon: <PdfIcon />,
        label: "Live Preview PDF →",
        href: (() => {
          if (typeof window === "undefined") return "#";
          const url = new URL(window.location.href);
          url.searchParams.set("web", "0");
          url.searchParams.delete("production");
          return url.toString();
        })(),
        style: { color: "#6600aa", background: "#ede5f7" },
        hideAtMicro: true,
      }] : []),
      {
        id: "togglepreview",
        icon: <RefreshIcon />,
        label: isParked ? "Resume Live Preview" : "Toggle Live Preview",
        onClick: onToggleLivePreview,
        title: isParked ? "Resume live preview" : "Pause live preview",
        style: isParked
          ? { color: "#166534", background: "#dcfce7", fontWeight: 700 }
          : { color: "#fff", background: "#e05a00", fontWeight: 700 },
        hideAtMicro: true,
      },
    ] : []),
    {
      id: "toc",
      icon: <OpenBookIcon />,
      label: contentsLabel,
      onClick: () => toggle("toc"),
      active: openPanel === "toc",
      title: contentsLabel || "Contents",
      hideAtMicro: false,
    },
  ];

  // Buttons visible in the bar at each breakpoint
  const barButtons = isMicro
    ? buttons.filter(b => !b.hideAtMicro)
    : buttons;

  // ── Manual title bar (micro only — <400px) ───────────────────────────────
  const MicroManualTitle = isMicro && (
    <div style={{
      display: "flex", alignItems: "center", gap: "6px",
      flex: 1, minWidth: 0, overflow: "hidden",
    }}>
      {logoUrl && (
        <img src={logoUrl} alt=""
          style={{ maxHeight: "20px", maxWidth: "48px", objectFit: "contain", flexShrink: 0 }} />
      )}
      {manualTitle && (
        <span style={{
          fontSize: "0.78rem", fontWeight: 600, color: "#222c47",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          fontFamily: font,
        }}>
          {manualTitle}
        </span>
      )}
    </div>
  );

  return (
    <div ref={navRef} style={{ position: "relative", fontFamily: font }}>
      {/* ── Button row ─────────────────────────────────────────────────── */}
      <div style={rowStyle}>
        {/* LEFT group */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          {/* Hamburger — compact only */}
          {isCompact && (
            <NavButton
              icon={openPanel === "menu" ? <CloseIcon /> : <HamburgerIcon />}
              showLabel={false}
              onClick={() => toggle("menu")}
              active={openPanel === "menu"}
              title="Menu"
              style={{ padding: "0.55rem 0.7rem" }}
            />
          )}

          {/* Contents — always visible at ≥640px */}
          {!isCompact && (
            <span ref={tocButtonRef} style={{ display: "contents" }}>
              <NavButton
                icon={<OpenBookIcon />}
                label={`${contentsLabel} ${openPanel === "toc" ? "▼" : "▾"}`}
                showLabel
                onClick={() => toggle("toc")}
                active={openPanel === "toc"}
                title={contentsLabel || "Contents"}
              />
            </span>
          )}

          {/* Language — NavButton at ≥640px (replaces LanguageSelector) */}
          {!isCompact && allSitesForSelector.length > 1 && (
            <span ref={langButtonRef} style={{ display: "contents" }}>
              <NavButton
                icon={<GoogleTranslateMenuIcon />}
                label={(() => {
                  const cur = allSitesForSelector.find(s => s.siteId === siteId);
                  const name = cur ? `${cur.name} (${cur.locale})` : langLabel;
                  return `${name} ${openPanel === "lang" ? "▼" : "▾"}`;
                })()}
                showLabel
                onClick={() => toggle("lang")}
                active={openPanel === "lang"}
                title={langLabel}
              />
            </span>
          )}

          {/* Preview page-action buttons (non-compact only) */}
          {!isCompact && isPreview && barButtons
            .filter(b => ["prod", "togglepreview", "importexport", "livepdf", "preview"].includes(b.id))
            .map(btn => (
              <NavButton
                key={btn.id}
                icon={btn.icon}
                label={btn.label}
                showLabel
                href={btn.href}
                onClick={btn.onClick ? (e) => { btn.onClick(e); } : undefined}
                active={btn.active}
                title={btn.title}
                style={btn.style}
              />
            ))
          }
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Micro: manual logo + title fills the gap */}
        {MicroManualTitle}

        {/* RIGHT group — external/navigation links */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          {!isMicro && (
            <NavButton
              icon={<ClosedBookIcon />}
              label={manualsLabel || "Manuals"}
              showLabel={showLabel}
              href="/"
              title={manualsLabel}
              externalBadge
            />
          )}

          {pdfUrl && (
            <NavButton
              icon={<PdfIcon />}
              label="PDF"
              showLabel={showLabel}
              href={pdfUrl}
              external
              title={t("web_pdf_download", "Download PDF")}
              externalBadge
            />
          )}

          {!isMicro && (
            <NavButton
              icon={<GlobeIcon />}
              label={webLabel || "Web"}
              showLabel={showLabel}
              href={webUrl}
              onClick={!isPreview ? (e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); } : undefined}
              title={t("web_web", "Web")}
              external={!!isPreview}
              externalBadge
            />
          )}

          {linkUrl && !isMicro && (
            <NavButton
              icon={<LinkIcon />}
              label={universalLabel || "Link"}
              showLabel={showLabel}
              href={linkUrl}
              title={universalLabel || "Universal link"}
              external
              externalBadge
            />
          )}

          {isPreview && previewUrl && !isMicro && (
            <NavButton
              icon={<EyeIcon />}
              label="Live Preview"
              showLabel={showLabel}
              href={previewUrl}
              title="Open in web preview mode"
              style={{ color: "#6600aa", background: "#ede5f7" }}
              external
              externalBadge
            />
          )}
        </div>
      </div>

      {/* Breadcrumb — visible below icons when hamburger is closed */}
      {openPanel !== "menu" && breadcrumbContent}

      {/* ── Hamburger ExpandPanel ─────────────────────────────────────── */}
      <ExpandPanel open={openPanel === "menu"} onClose={close} minWidth="100%" maxWidth="100%" style={{ maxHeight: "70vh" }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {/* Breadcrumb as first item inside hamburger */}
          {breadcrumbContent && (
            <div style={{ borderBottom: "1px solid #eee" }}>
              {breadcrumbContent}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "2px", padding: "4px" }}>
            {/* Language — FIRST, with ▾ expand indicator */}
            {allSitesForSelector.length > 1 && (
              <div>
                <NavButton
                  icon={<GoogleTranslateMenuIcon />}
                  label={(() => {
                    const cur = allSitesForSelector.find(s => s.siteId === siteId);
                    const name = cur ? `${cur.name} (${cur.locale})` : langLabel;
                    return `${name} ▾`;
                  })()}
                  showLabel
                  onClick={() => setLangSubOpen(p => !p)}
                  active={langSubOpen}
                  title={langLabel}
                  style={{ borderRadius: "6px", width: "100%", justifyContent: "flex-start" }}
                />
                {langSubOpen && (
                  <LangPanel
                    sites={allSitesForSelector}
                    currentSiteId={siteId}
                    onSelect={(newSiteId) => { if (onLangChange) onLangChange(newSiteId); close(); }}
                    maxHeight="35vh"
                  />
                )}
              </div>
            )}

            {/* Home */}
            <NavButton icon={<ClosedBookIcon />} label={manualsLabel} showLabel href="/" title={manualsLabel}
              style={{ borderRadius: "6px", width: "100%", justifyContent: "flex-start" }} />

            {/* PDF */}
            {pdfUrl && <NavButton icon={<PdfIcon />} label="PDF" showLabel href={pdfUrl} external title={t("web_pdf_download", "Download PDF")}
              style={{ borderRadius: "6px", width: "100%", justifyContent: "flex-start" }} />}

            {/* Web */}
            {webUrl && <NavButton icon={<GlobeIcon />} label={webLabel || "Web"} showLabel href={webUrl}
              onClick={!isPreview ? (e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); close(); } : undefined}
              external={!!isPreview}
              title={t("web_web", "Web")} style={{ borderRadius: "6px", width: "100%", justifyContent: "flex-start" }} />}

            {/* Universal */}
            {linkUrl && <NavButton icon={<LinkIcon />} label={universalLabel || "Link"} showLabel href={linkUrl} external
              title={universalLabel || "Universal link"} style={{ borderRadius: "6px", width: "100%", justifyContent: "flex-start" }} />}

            {/* Preview-only buttons */}
            {isPreview && barButtons.filter(b => ["prod", "togglepreview", "importexport", "livepdf", "preview"].includes(b.id)).map(btn => (
              <NavButton key={btn.id} icon={btn.icon} label={btn.label} showLabel href={btn.href}
                onClick={btn.onClick ? (e) => { btn.onClick(e); close(); } : undefined}
                active={btn.active} title={btn.title}
                style={{ ...btn.style, borderRadius: "6px", width: "100%", justifyContent: "flex-start" }} />
            ))}
          </div>

          {/* TOC — collapsible section */}
          {chapterGroups.length > 0 && (
            <div style={{ borderTop: "1px solid #eee" }}>
              {/* TOC header with collapse button */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 12px 4px" }}>
                <span style={flyoutSectionHeadStyle}>Contents</span>
                <button
                  type="button"
                  onClick={() => setTocSubOpen(p => !p)}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: "0.75rem", color: "#888", padding: "2px 6px",
                    fontFamily: font, display: "flex", alignItems: "center", gap: "4px",
                  }}
                >
                  {tocSubOpen ? "▲ Collapse" : "▼ Expand"}
                </button>
              </div>
              {tocSubOpen && (
                <TocPanel chapterGroups={chapterGroups} onNavigate={close} />
              )}
            </div>
          )}
        </div>
      </ExpandPanel>

      {/* TOC panel — inline, in-flow, only at ≥640px */}
      {!isCompact && (
        <ExpandPanel
          open={openPanel === "toc"}
          onClose={close}
          excludeRef={tocButtonRef}
          minWidth="280px"
          maxWidth="100%"
        >
          <div>
            <div style={flyoutSectionHeadStyle}>Contents</div>
            <TocPanel chapterGroups={chapterGroups} onNavigate={close} maxHeight="60vh" />
          </div>
        </ExpandPanel>
      )}

      {/* Language panel — inline, in-flow, only at ≥640px */}
      {!isCompact && allSitesForSelector.length > 1 && (
        <ExpandPanel
          open={openPanel === "lang"}
          onClose={close}
          excludeRef={langButtonRef}
          minWidth="240px"
          maxWidth="100%"
        >
          <LangPanel
            sites={allSitesForSelector}
            currentSiteId={siteId}
            onSelect={(newSiteId) => { if (onLangChange) onLangChange(newSiteId); close(); }}
          />
        </ExpandPanel>
      )}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────

const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  padding: "0.4rem 0.75rem 0.6rem",
  fontFamily: font,
  flexWrap: "wrap",
};

const flyoutSectionHeadStyle = {
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: "#888",
  padding: "8px 12px 4px",
  fontFamily: font,
};
